import { Edit2, ExternalLink, Zap } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Sheet, SheetContent } from "@/marketing/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/marketing/components/ui/popover";
import {
  type InfluencerCampaign,
  InfluencerCampaignStatus,
  STATUS_META,
  STATUS_ORDER,
  TRAFFIC_DOT,
} from "@/marketing/models/influencerCampaign";

import {
  formatDate,
  initials,
  progressPct,
  safeHostname,
} from "@/marketing/lib/formatters";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="tw-flex tw-justify-between">
      <span className="tw-text-muted-foreground">{k}</span>
      <span className="tw-font-medium">{v}</span>
    </div>
  );
}

function TargetBlock({
  label,
  current,
  total,
  color,
  accent,
}: {
  label: string;
  current: number;
  total: number | undefined;
  color: string;
  accent: string;
}) {
  const pct = progressPct(current, total);
  return (
    <div className="tw-rounded-md tw-border tw-border-border tw-bg-muted/30 tw-p-3">
      <div className="tw-mb-1.5 tw-flex tw-justify-between">
        <span className="tw-text-[13px] tw-font-medium">{label}</span>
        <span className={cn("tw-text-[13px] tw-font-semibold", accent)}>
          {current} / {total ?? 0}
        </span>
      </div>
      <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
        <div
          className={cn("tw-h-full tw-rounded-full", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="tw-mt-1.5 tw-text-[11px] tw-text-muted-foreground">{pct}%</div>
    </div>
  );
}

interface CampaignDetailSheetProps {
  selected: InfluencerCampaign | null;
  onClose: () => void;
  onStatusChange: (status: InfluencerCampaignStatus) => void | Promise<void>;
  onEdit?: () => void;
}

export function CampaignDetailSheet({
  selected,
  onClose,
  onStatusChange,
  onEdit,
}: CampaignDetailSheetProps) {
  const lookupBrand = useBrandLookup();
  return (
    <Sheet open={!!selected} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="tw-w-[480px] tw-overflow-y-auto tw-p-0 sm:tw-max-w-[480px]">
        {selected && (
          <div className="tw-flex tw-h-full tw-flex-col">
            <div className="tw-border-b tw-border-border tw-p-5 tw-pr-12">
              <h2 className="tw-text-base tw-font-semibold tw-leading-snug">
                {selected.name}
              </h2>
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium">
                  {lookupBrand(selected.brandId)?.name ??
                    selected.brandId.slice(0, 8)}
                </span>
                {selected.trafficLight && (
                  <span
                    className={cn(
                      "tw-h-2.5 tw-w-2.5 tw-rounded-full",
                      TRAFFIC_DOT[selected.trafficLight],
                    )}
                  />
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        "tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium",
                        STATUS_META[selected.status].pill,
                      )}
                    >
                      {STATUS_META[selected.status].label}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="tw-w-44 tw-p-1">
                    {STATUS_ORDER.map((status) => (
                      <button
                        key={status}
                        onClick={() => onStatusChange(status)}
                        className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-left tw-text-xs hover:tw-bg-accent"
                      >
                        <span
                          className={cn(
                            "tw-h-1.5 tw-w-1.5 tw-rounded-full",
                            STATUS_META[status].pill,
                          )}
                        />
                        {STATUS_META[status].label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="tw-flex-1 tw-space-y-6 tw-overflow-y-auto tw-p-5 tw-text-sm">
              <Section label="Brief">
                <p className="tw-leading-relaxed tw-text-foreground/80">
                  {selected.brief || "—"}
                </p>
              </Section>

              <Section label="Targets">
                <div className="tw-space-y-2.5">
                  <TargetBlock
                    label="Onboarding"
                    current={0}
                    total={selected.onboardingTarget}
                    color="tw-bg-violet-600"
                    accent="tw-text-violet-600"
                  />
                  <TargetBlock
                    label="Posts published"
                    current={0}
                    total={selected.postTarget}
                    color="tw-bg-emerald-500"
                    accent="tw-text-emerald-600"
                  />
                </div>
              </Section>

              <Section label="Schedule">
                <div className="tw-space-y-1.5 tw-text-[13px]">
                  <KV k="Start date" v={formatDate(selected.startAt)} />
                  <KV k="End date" v={formatDate(selected.endAt)} />
                  <KV k="Posting due" v={formatDate(selected.postingDueDate)} />
                </div>
              </Section>

              {selected.productRefs && selected.productRefs.length > 0 && (
                <Section label="Product references">
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    {selected.productRefs.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-border tw-bg-background tw-px-2 tw-py-0.5 tw-text-[12px] tw-text-foreground/80 hover:tw-bg-accent"
                      >
                        <ExternalLink className="tw-h-3 tw-w-3" />
                        {safeHostname(url)}
                      </a>
                    ))}
                  </div>
                </Section>
              )}

              <Section label="Owner / PIC">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                  <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-violet-600 tw-text-xs tw-font-semibold tw-text-white">
                    {initials(selected.ownerId)}
                  </div>
                  <div className="tw-text-[13px]">
                    <div className="tw-font-medium">{selected.ownerId}</div>
                    <div className="tw-text-xs tw-text-muted-foreground">
                      Campaign Manager
                    </div>
                  </div>
                </div>
              </Section>

              {selected.consideration && (
                <Section label="Consideration (internal)">
                  <div className="tw-rounded-md tw-border tw-border-border tw-bg-muted/40 tw-p-3 tw-text-[13px] tw-leading-relaxed tw-text-foreground/80">
                    {selected.consideration}
                  </div>
                </Section>
              )}
            </div>

            <div className="tw-flex tw-flex-shrink-0 tw-flex-col tw-gap-2 tw-border-t tw-border-border tw-p-4">
              {onEdit && (
                <Button
                  variant="outline"
                  className="tw-w-full tw-gap-1.5"
                  onClick={onEdit}
                >
                  <Edit2 className="tw-h-3.5 tw-w-3.5" />
                  Edit campaign
                </Button>
              )}
              <div className="tw-flex tw-gap-2">
                <Button variant="outline" className="tw-flex-1">
                  Archive
                </Button>
                <Button className="tw-flex-1 tw-gap-1.5 tw-bg-violet-600 tw-text-white hover:tw-bg-violet-700">
                  <Zap className="tw-h-3.5 tw-w-3.5" />
                  Open matcher
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
